import { Box, Paper, Typography, Grid, LinearProgress, Chip } from '@mui/material';
import { CheckCircle, Warning, Schedule, TrendingUp } from '@mui/icons-material';
import { InvestmentChecklistDTO } from '@inversor-hub/types';
import {
  calculateOverallProgress,
  getOverdueTasks,
  getNextDueTask,
  formatDueDate,
} from './checklist-utils';

interface ChecklistStatsHeaderProps {
  checklist: InvestmentChecklistDTO;
  onViewOverdueTasks?: () => void;
}

const ChecklistStatsHeader = ({ checklist, onViewOverdueTasks }: ChecklistStatsHeaderProps) => {
  const overallProgress = calculateOverallProgress(checklist);
  const overdueTasks = getOverdueTasks(checklist.tasks);
  const nextTask = getNextDueTask(checklist.tasks);

  const isOverdueClickable = overdueTasks.length > 0 && onViewOverdueTasks;

  const stats = [
    {
      icon: <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />,
      label: 'Completadas',
      value: `${overallProgress.completed}/${overallProgress.total}`,
      color: 'success.main',
    },
    {
      icon: <Warning sx={{ fontSize: 40, color: 'error.main' }} />,
      label: 'Vencidas',
      value: overdueTasks.length,
      color: 'error.main',
      clickable: isOverdueClickable,
      onClick: onViewOverdueTasks,
      hint: isOverdueClickable ? 'Ver detalles' : undefined,
    },
    {
      icon: <Schedule sx={{ fontSize: 40, color: 'info.main' }} />,
      label: 'Próximo vencimiento',
      value: nextTask ? formatDueDate(nextTask.dueAt!) : 'N/A',
      subtitle: nextTask?.label,
      color: 'info.main',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />,
      label: 'Progreso total',
      value: `${overallProgress.percentage}%`,
      color: 'primary.main',
    },
  ];

  return (
    <Box mb={4}>
      <Box
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1}
        mb={2}
        sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
      >
        <Typography variant="h5" fontWeight={700}>
          Itinerario de Inversión
        </Typography>
        <Chip
          label={`${overallProgress.percentage}% Completado`}
          color={overallProgress.percentage === 100 ? 'success' : 'primary'}
          sx={{ fontWeight: 600, fontSize: '0.875rem' }}
        />
      </Box>

      <Box mb={2}>
        <LinearProgress
          variant="determinate"
          value={overallProgress.percentage}
          sx={{
            height: 12,
            borderRadius: 2,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 2,
              bgcolor: overallProgress.percentage === 100 ? 'success.main' : 'primary.main',
            },
          }}
        />
      </Box>

      <Grid container spacing={2}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Paper
              onClick={stat.clickable ? stat.onClick : undefined}
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                cursor: stat.clickable ? 'pointer' : 'default',
                border: 2,
                borderColor: 'transparent',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                  ...(stat.clickable && {
                    borderColor: 'error.main',
                  }),
                },
              }}
            >
              {stat.icon}
              <Typography variant="body2" color="text.secondary" mt={1}>
                {stat.label}
              </Typography>
              <Typography variant="h5" fontWeight={700} color={stat.color}>
                {stat.value}
              </Typography>
              {stat.subtitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mt={0.5}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {stat.subtitle}
                </Typography>
              )}
              {stat.hint && (
                <Typography variant="caption" color="error.main" mt={0.5} fontWeight={500}>
                  {stat.hint}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ChecklistStatsHeader;
